export const DELIMITER_OPTIONS = [
    { value: ',', label: 'Comma-separated (CSV)' },
    { value: '\t', label: 'Tab-separated (TSV)' },
    { value: ';', label: 'Semicolon-separated' },
    { value: '|', label: 'Pipe-separated' },
];

function stripByteOrderMark(text) {
    return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

export function parseDelimitedRows(input, delimiter) {
    if (!delimiter || delimiter.length !== 1) {
        throw new Error('A one-character delimiter is required.');
    }

    const text = stripByteOrderMark(String(input || ''));
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    for (let index = 0; index < text.length; index += 1) {
        const character = text[index];

        if (character === '"') {
            if (inQuotes && text[index + 1] === '"') {
                field += '"';
                index += 1;
            } else if (inQuotes) {
                inQuotes = false;
            } else if (field.length === 0) {
                inQuotes = true;
            } else {
                field += character;
            }
        } else if (character === delimiter && !inQuotes) {
            row.push(field);
            field = '';
        } else if ((character === '\n' || character === '\r') && !inQuotes) {
            row.push(field);
            rows.push(row);
            row = [];
            field = '';

            if (character === '\r' && text[index + 1] === '\n') {
                index += 1;
            }
        } else {
            field += character;
        }
    }

    if (inQuotes) {
        throw new Error('The file contains an unclosed quoted value.');
    }

    if (field.length > 0 || row.length > 0) {
        row.push(field);
        rows.push(row);
    }

    return rows;
}

function delimiterScore(text, delimiter) {
    try {
        const rows = parseDelimitedRows(text, delimiter)
            .filter(row => row.some(value => value.trim() !== ''))
            .slice(0, 8);
        const widths = rows.map(row => row.length).filter(width => width > 1);
        if (widths.length === 0) return 0;

        const counts = widths.reduce((result, width) => ({
            ...result,
            [width]: (result[width] || 0) + 1,
        }), {});
        const [mostCommonWidth, matches] = Object.entries(counts)
            .sort((left, right) => right[1] - left[1])[0];

        return matches * 100 + Number(mostCommonWidth);
    } catch (error) {
        return 0;
    }
}

export function detectDelimiter(text, fileName = '') {
    const extension = fileName.toLowerCase().split('.').pop();
    const extensionPreference = extension === 'tsv' || extension === 'tab'
        ? '\t'
        : extension === 'psv'
            ? '|'
            : extension === 'csv'
                ? ','
                : null;
    const scored = DELIMITER_OPTIONS.map(({ value }, index) => ({
        value,
        score: delimiterScore(text, value),
        preference: value === extensionPreference ? 1 : 0,
        index,
    })).sort((left, right) => (
        right.score - left.score
        || right.preference - left.preference
        || left.index - right.index
    ));

    return scored[0]?.score > 0 ? scored[0].value : (extensionPreference || ',');
}

export function parseDelimitedText(input, options = {}) {
    const text = stripByteOrderMark(String(input || ''));
    if (!text.trim()) {
        throw new Error('The selected file is empty.');
    }

    const delimiter = options.delimiter || detectDelimiter(text, options.fileName);
    const parsedRows = parseDelimitedRows(text, delimiter)
        .filter(row => row.some(value => value.trim() !== ''));
    if (parsedRows.length < 2) {
        throw new Error('The file must contain a header row and at least one data row.');
    }

    const headerValues = parsedRows[0];
    const headers = headerValues.map((value, index) => ({
        id: `column-${index}`,
        label: value.trim() || `Column ${index + 1}`,
        index,
    }));
    const rows = parsedRows.slice(1).map(row => headers.map(header => row[header.index] || ''));

    return { delimiter, headers, rows };
}

function normalizeName(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function autoMapFields(fields, headers) {
    const usedHeaders = new Set();

    return fields.reduce((mappings, field) => {
        const candidates = [field.name, field.label, ...(field.aliases || [])]
            .map(normalizeName)
            .filter(Boolean);
        const header = headers.find(candidate => (
            !usedHeaders.has(candidate.id)
            && candidates.includes(normalizeName(candidate.label))
        ));

        if (header) {
            mappings[field.name] = header.id;
            usedHeaders.add(header.id);
        }

        return mappings;
    }, {});
}
